import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { cdApi } from '../../api/changeDetection'
import CdRegionModeToggle from './CdRegionModeToggle'
import type {
  CdRegionMode,
  CdDiff,
  CdDiffCategory,
  CdDiffItem,
  CdDiffVisible,
  CdObservation,
  CdReport,
  CdReportFormat,
} from '../../types/changeDetection'

// ── 결과 표시용 포맷터·범례 (voxel-based-change-detection RightPanel.jsx 참조) ──
function fmtVol(m3: number | null | undefined): string {
  if (m3 == null || Number.isNaN(m3)) return '—'
  const a = Math.abs(m3)
  if (a >= 1_000_000) return `${(m3 / 1_000_000).toFixed(3)} km³`
  if (a >= 1_000) return `${(m3 / 1_000).toFixed(2)}k m³`
  return `${m3.toFixed(1)} m³`
}

const RESULT_LAYERS = [
  { key: 'add', tag: 'ADD', dot: 'bg-emerald-500', tagCls: 'bg-emerald-50 text-emerald-700' },
  { key: 'rem', tag: 'REM', dot: 'bg-red-500', tagCls: 'bg-red-50 text-red-700' },
  { key: 'chg', tag: 'CHG', dot: 'bg-amber-500', tagCls: 'bg-amber-50 text-amber-700' },
] as const

// 시점쌍 1건의 변화탐지 결과 — 날짜 헤더 + 색상 범례 + 통계 박스
function DiffItemResult({ item }: { item: CdDiffItem }) {
  const { t } = useTranslation()
  const added = item.addedVolume ?? 0
  const removed = item.removedVolume ?? 0
  const changed = item.changedVolume ?? 0
  const net = added - removed

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
      <div className="text-[11px] font-medium text-slate-500">
        {item.sourceObservedAt} <span className="text-slate-300">→</span> {item.targetObservedAt}
      </div>

      <div className="mt-2 space-y-1">
        {RESULT_LAYERS.map((l) => (
          <div key={l.key} className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-sm ${l.dot}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium text-slate-700">{t(`cd.layer.${l.key}.name`)}</div>
              <div className="text-[10px] text-slate-400">{t(`cd.layer.${l.key}.desc`)}</div>
            </div>
            <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${l.tagCls}`}>{l.tag}</span>
          </div>
        ))}
      </div>

      <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
        <div className="flex justify-between text-[11px]">
          <span className="text-slate-500">{t('cd.diff.added')}</span>
          <span className="font-semibold text-emerald-600">+{fmtVol(added)}</span>
        </div>
        <div className="flex justify-between text-[11px]">
          <span className="text-slate-500">{t('cd.diff.removed')}</span>
          <span className="font-semibold text-red-600">-{fmtVol(removed)}</span>
        </div>
        {changed > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-slate-500">{t('cd.diff.kept')}</span>
            <span className="font-semibold text-amber-600">{fmtVol(changed)}</span>
          </div>
        )}
        <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-[11px]">
          <span className="font-medium text-slate-600">{t('cd.diff.netChange')}</span>
          <span className={`font-bold ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {net >= 0 ? '+' : ''}
            {fmtVol(net)}
          </span>
        </div>
      </div>
    </div>
  )
}

// 시계열 diff — 시점쌍별 순 변화(추가 − 제거) 인라인 SVG 막대 차트
function TimeSeriesBars({ items }: { items: CdDiffItem[] }) {
  const { t } = useTranslation()
  const rows = items
    .filter((it) => it.status === 'SUCCEEDED')
    .map((it) => ({
      label: String(it.targetObservedAt).slice(2),
      net: (it.addedVolume ?? 0) - (it.removedVolume ?? 0),
    }))
  if (rows.length < 2) return null
  const max = Math.max(...rows.map((r) => Math.abs(r.net)), 1)
  const W = 260
  const H = 96
  const pad = 16
  const zero = H / 2
  const slot = (W - pad * 2) / rows.length
  const bw = Math.min(22, slot * 0.6)
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2">
      <div className="text-[10px] font-medium text-slate-500 mb-1">{t('cd.diff.pairNetChange')}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={t('cd.diff.chartAria')}>
        <line x1={pad} y1={zero} x2={W - pad} y2={zero} stroke="#cbd5e1" strokeWidth="1" />
        {rows.map((r, i) => {
          const cx = pad + slot * (i + 0.5)
          const h = (Math.abs(r.net) / max) * (zero - 8)
          const up = r.net >= 0
          return (
            <g key={i}>
              <rect
                x={cx - bw / 2}
                y={up ? zero - h : zero}
                width={bw}
                height={Math.max(h, 1)}
                fill={up ? '#22c55e' : '#ef4444'}
                rx="1.5"
              />
              <text x={cx} y={H - 3} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 7 }}>
                {r.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

type CompareKind = 'original' | 'voxel'

interface Props {
  projectId: number
  observations: CdObservation[]
  selectedIds: number[]
  diffs: CdDiff[]
  shownDiffId: number | null
  diffVisible: CdDiffVisible
  onChanged: () => void
  onToggleShow: (diffId: number) => void
  onToggleDiffVisible: (cat: CdDiffCategory) => void
  reportsByDiff: Record<number, CdReport[]>
  reportBusy: Record<string, boolean>
  onLoadReports: (diffId: number) => void
  onCreateReport: (diffId: number, format: CdReportFormat) => void
  onDownloadReport: (diffId: number, report: CdReport) => void
  onDeleteReport: (diffId: number, reportId: number) => void
  compare: { diffId: number; mode: 'blink' | 'split'; kinds: CompareKind[] } | null
  onSetCompare: (diffId: number, mode: 'blink' | 'split' | null, kinds: CompareKind[]) => void
  /** 상세 floating 창을 올릴 지도 컨테이너 */
  mapContainer: HTMLElement | null
  /** 프로젝트 분석 영역(GPKG)이 있으면 WKT 영역 지정은 쓸 수 없다 */
  areaWkt: string | null
  areaDrawing: boolean
  onStartDrawArea: (mode: CdRegionMode) => void
  onClearArea: () => void
}

const ACTIVE = new Set(['QUEUED', 'RUNNING'])

/** 결과 목록용 요약 — 성공한 시점쌍의 추가/제거 체적 합과 순 변화 */
function summarize(d: CdDiff) {
  const done = d.items.filter((it) => it.status === 'SUCCEEDED')
  const added = done.reduce((a, it) => a + (it.addedVolume ?? 0), 0)
  const removed = done.reduce((a, it) => a + (it.removedVolume ?? 0), 0)
  return { done: done.length, added, removed, net: added - removed }
}

/** 지도 위에 떠 있는 상세 창. 헤더를 드래그해 옮길 수 있다. */
function FloatingWindow({
  title,
  container,
  onClose,
  children,
}: {
  title: string
  container: HTMLElement
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    const el = ref.current
    if (!el) return
    const box = el.getBoundingClientRect()
    const cb = container.getBoundingClientRect()
    const dx = e.clientX - box.left
    const dy = e.clientY - box.top
    const move = (ev: PointerEvent) =>
      setPos({
        x: Math.min(Math.max(0, ev.clientX - cb.left - dx), Math.max(0, cb.width - 80)),
        y: Math.min(Math.max(0, ev.clientY - cb.top - dy), Math.max(0, cb.height - 40)),
      })
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return createPortal(
    <div
      ref={ref}
      className="absolute z-30 flex max-h-[75%] w-80 flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-300"
      style={pos ? { left: pos.x, top: pos.y } : { right: 12, top: 56 }}
    >
      <div
        onPointerDown={onPointerDown}
        className="flex shrink-0 cursor-move select-none items-center justify-between bg-slate-800 px-3 py-1.5 text-white"
      >
        <span className="truncate text-xs font-semibold">{title}</span>
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          aria-label={t('cd.diff.detailClose')}
          className="ml-2 text-sm leading-none text-slate-300 hover:text-white"
        >
          ×
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
    </div>,
    container,
  )
}

export default function CdDiffPanel({
  projectId,
  observations,
  selectedIds,
  diffs,
  shownDiffId,
  diffVisible,
  onChanged,
  onToggleShow,
  onToggleDiffVisible,
  reportsByDiff,
  reportBusy,
  onLoadReports,
  onCreateReport,
  onDownloadReport,
  onDeleteReport,
  compare,
  onSetCompare,
  mapContainer,
  areaWkt,
  areaDrawing,
  onStartDrawArea,
  onClearArea,
}: Props) {
  const { t } = useTranslation()
  const [compareKinds, setCompareKinds] = useState<CompareKind[]>(['original'])
  const [mode, setMode] = useState<'ab' | 'timeseries'>('ab')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // 분석 영역: 직접 그리기(WKT) 또는 GeoPackage 중 하나 + 포함/제외 모드
  const [areaSource, setAreaSource] = useState<'wkt' | 'gpkg'>('wkt')
  const [areaMode, setAreaMode] = useState<CdRegionMode>('INCLUDE')
  const [areaGpkg, setAreaGpkg] = useState<{ key: string; name: string } | null>(null)
  const [areaGpkgBusy, setAreaGpkgBusy] = useState(false)
  const areaGpkgInputRef = useRef<HTMLInputElement | null>(null)
  const [renameId, setRenameId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // 선택(지도 표시)된 결과의 보고서 목록을 불러온다.
  useEffect(() => {
    if (shownDiffId != null) onLoadReports(shownDiffId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownDiffId])

  const readyCount = observations.filter((o) => o.voxelStatus === 'SUCCEEDED').length
  const canSubmit = mode === 'ab' ? selectedIds.length === 2 : readyCount >= 2

  const handleSubmit = async () => {
    setError('')
    if (!name.trim()) {
      setError(t('cd.diff.nameRequired'))
      return
    }
    setSubmitting(true)
    try {
      const area =
        areaSource === 'wkt'
          ? areaWkt
            ? { areaWkt, areaMode }
            : {}
          : areaGpkg
            ? { areaGpkgKey: areaGpkg.key, areaGpkgFileName: areaGpkg.name, areaMode }
            : {}
      if (mode === 'ab') {
        await cdApi.createAbDiff(projectId, {
          name: name.trim(),
          sourceObservationId: selectedIds[0],
          targetObservationId: selectedIds[1],
          ...area,
        })
      } else {
        await cdApi.createTimeSeriesDiff(projectId, { name: name.trim(), ...area })
      }
      setName('')
      onChanged()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? t('cd.diff.requestFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleAreaSource = (next: 'wkt' | 'gpkg') => {
    if (next === areaSource) return
    // 한 번에 하나의 소스만 쓴다 - 다른 쪽 선택은 비운다.
    if (next === 'gpkg') onClearArea()
    else setAreaGpkg(null)
    setAreaSource(next)
  }

  const handleAreaGpkgPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setAreaGpkgBusy(true)
    setError('')
    try {
      const res = await cdApi.uploadDiffRegionGpkg(projectId, file)
      setAreaGpkg({ key: res.objectKey, name: res.fileName })
    } catch (err: unknown) {
      const x = err as { response?: { data?: { error?: string } } }
      setError(x.response?.data?.error ?? t('cd.area.gpkgUploadFailed'))
    } finally {
      setAreaGpkgBusy(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t('cd.diff.deleteConfirm'))) return
    try {
      await cdApi.deleteDiff(id)
      onChanged()
    } catch {
      alert(t('cd.diff.deleteFailed'))
    }
  }

  const handleCancel = async (id: number) => {
    try {
      await cdApi.cancelDiff(id)
      onChanged()
    } catch {
      alert(t('cd.diff.cancelFailed'))
    }
  }

  const handleRename = async (id: number) => {
    const next = renameValue.trim()
    if (!next) return
    try {
      await cdApi.updateDiff(id, { name: next })
      setRenameId(null)
      onChanged()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      alert(err.response?.data?.error ?? t('cd.diff.renameFailed'))
    }
  }

  const detail = diffs.find((d) => d.id === shownDiffId) ?? null
  const detailItems = detail?.items.filter((it) => it.status === 'SUCCEEDED') ?? []

  // ── floating 상세 창의 내용 ──
  const renderDetail = (d: CdDiff) => {
    const obsOf = (id: number) => observations.find((o) => o.id === id)
    // voxel 비교는 모든 쌍의 두 관측이 실제 voxel 타일을 가질 때만 가능하다
    const voxelReady = detailItems.every(
      (it) => !!obsOf(it.sourceObservationId)?.voxelTilesetUrl && !!obsOf(it.targetObservationId)?.voxelTilesetUrl,
    )
    const kinds = compare?.diffId === d.id ? compare.kinds : compareKinds
    const reports = reportsByDiff[d.id] ?? []

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {RESULT_LAYERS.map((l) => {
            const on = diffVisible[l.key]
            return (
              <button
                key={l.key}
                onClick={() => onToggleDiffVisible(l.key)}
                title={on ? t('cd.diff.hideOnMap') : t('cd.diff.showOnMap')}
                className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium transition ${
                  on
                    ? 'border-slate-300 bg-white text-slate-700'
                    : 'border-slate-200 bg-slate-100 text-slate-400 line-through'
                }`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-sm ${l.dot} ${on ? '' : 'opacity-30'}`} />
                {t(`cd.layer.${l.key}.name`)}
              </button>
            )
          })}
        </div>

        {(d.areaWkt || d.areaGpkgFileName) && (
          <p className="rounded bg-violet-50 px-2 py-1 text-[10px] text-violet-700">
            {t(d.areaMode === 'EXCLUDE' ? 'cd.diff.areaResultExclude' : 'cd.diff.areaResultInclude', {
              source: d.areaWkt
                ? t('cd.diff.areaSourceWkt')
                : t('cd.diff.areaSourceGpkg', { name: d.areaGpkgFileName ?? '' }),
            })}
          </p>
        )}

        {detailItems.map((it) => (
          <DiffItemResult key={it.id} item={it} />
        ))}
        {d.type === 'TIME_SERIES' && <TimeSeriesBars items={d.items} />}

        <div className="rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-600">{t('cd.compare.title')}</span>
            <div className="flex gap-1.5">
              {(['blink', 'split'] as const).map((m) => {
                const active = compare?.diffId === d.id && compare.mode === m
                return (
                  <button
                    key={m}
                    onClick={() => onSetCompare(d.id, active ? null : m, kinds)}
                    disabled={kinds.includes('voxel') && !voxelReady}
                    className={`rounded border px-2 py-0.5 text-[10px] font-medium transition disabled:opacity-40 ${
                      active
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {m === 'blink' ? t('cd.compare.blink') : t('cd.compare.split')}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400">{t('cd.compare.target')}</span>
            {(['original', 'voxel'] as const).map((k) => (
              <button
                key={k}
                onClick={() => {
                  // 복수 선택 — 최소 1개는 유지한다.
                  const next: CompareKind[] = kinds.includes(k)
                    ? kinds.filter((x) => x !== k)
                    : (['original', 'voxel'] as const).filter((x) => x === k || kinds.includes(x))
                  if (next.length === 0) return
                  setCompareKinds(next)
                  // 비교 중이면 같은 모드로 대상만 교체
                  if (compare?.diffId === d.id) onSetCompare(d.id, compare.mode, next)
                }}
                disabled={k === 'voxel' && !voxelReady}
                aria-pressed={kinds.includes(k)}
                className={`rounded border px-2 py-0.5 text-[10px] font-medium transition disabled:opacity-40 ${
                  kinds.includes(k)
                    ? 'border-slate-700 bg-slate-700 text-white'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {k === 'original' ? '3D Tiles' : 'voxel'}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            {compare?.diffId === d.id
              ? compare.mode === 'blink'
                ? t('cd.compare.hintBlink')
                : t('cd.compare.hintSplit')
              : d.type === 'TIME_SERIES'
                ? t('cd.compare.hintTimeSeries')
                : t('cd.compare.hintAb')}
            {!voxelReady && t('cd.compare.noVoxel')}
          </p>
        </div>

        {(d.status === 'SUCCEEDED' || d.status === 'PARTIAL_FAILED') && (
          <div className="rounded-lg border border-slate-200 bg-white p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-600">{t('cd.report.title')}</span>
              <div className="flex gap-1.5">
                {(['PDF', 'HWPX'] as const).map((fmt) => {
                  const busy = reportBusy[`${d.id}:${fmt}`]
                  return (
                    <button
                      key={fmt}
                      onClick={() => onCreateReport(d.id, fmt)}
                      disabled={busy}
                      className="rounded border border-blue-300 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                    >
                      {busy ? t('cd.report.generating') : t('cd.report.generate', { format: fmt })}
                    </button>
                  )
                })}
              </div>
            </div>
            {reports.length > 0 && (
              <ul className="mt-2 space-y-1">
                {reports.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="min-w-0 truncate text-slate-500">
                      <span className="font-semibold text-slate-700">{r.format}</span>
                      {' · '}
                      {r.createdAt?.slice(0, 16).replace('T', ' ')}
                      {' · '}
                      <span className={r.status === 'FAILED' ? 'text-red-600' : 'text-slate-500'}>
                        {t(`cd.status.report.${r.status}`)}
                      </span>
                    </span>
                    <span className="flex shrink-0 gap-2">
                      {r.status === 'SUCCEEDED' && (
                        <button
                          onClick={() => onDownloadReport(d.id, r)}
                          className="font-medium text-blue-600 hover:text-blue-800"
                        >
                          {t('cd.report.download')}
                        </button>
                      )}
                      <button onClick={() => onDeleteReport(d.id, r.id)} className="text-slate-400 hover:text-red-600">
                        {t('common.delete')}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-slate-800">{t('cd.diff.runTitle')}</h3>
        <div className="mt-2 flex gap-2">
          {(['ab', 'timeseries'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                mode === m ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {m === 'ab' ? t('cd.diff.modeAb') : t('cd.diff.modeTimeSeries')}
            </button>
          ))}
        </div>

        <p className="mt-2 text-xs text-slate-500">
          {mode === 'ab'
            ? t('cd.diff.abHint', { count: selectedIds.length })
            : t('cd.diff.tsHint', { count: readyCount })}
        </p>

        <div className="mt-2 space-y-1 text-[11px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-600">{t('cd.area.title')}</span>
            <span className="inline-flex overflow-hidden rounded border border-slate-300 text-[10px] font-medium">
              {(['wkt', 'gpkg'] as const).map((src) => (
                <button
                  key={src}
                  type="button"
                  aria-pressed={areaSource === src}
                  onClick={() => handleAreaSource(src)}
                  className={`px-1.5 py-0.5 transition ${
                    areaSource === src ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {src === 'wkt' ? t('cd.area.sourceDraw') : t('cd.area.sourceGpkg')}
                </button>
              ))}
            </span>
            <CdRegionModeToggle value={areaMode} onChange={setAreaMode} disabled={areaDrawing} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {areaSource === 'wkt' ? (
              areaDrawing ? (
                <span className="text-blue-600">{t('cd.area.drawHint')}</span>
              ) : areaWkt ? (
                <>
                  <span className={areaMode === 'EXCLUDE' ? 'text-red-500' : 'text-emerald-600'}>
                    {t('cd.area.isSet', { mode: areaMode === 'EXCLUDE' ? t('common.exclude') : t('common.include') })}
                  </span>
                  <button onClick={() => onStartDrawArea(areaMode)} className="text-blue-600 hover:text-blue-800">
                    {t('common.redraw')}
                  </button>
                  <button onClick={onClearArea} className="text-red-500 hover:text-red-700">
                    {t('common.clear')}
                  </button>
                </>
              ) : (
                <button onClick={() => onStartDrawArea(areaMode)} className="text-blue-600 hover:text-blue-800">
                  {t('common.drawArea')}
                </button>
              )
            ) : areaGpkg ? (
              <>
                <span className="max-w-[160px] truncate text-slate-700" title={areaGpkg.name}>
                  {areaGpkg.name}
                </span>
                <button onClick={() => setAreaGpkg(null)} className="text-red-500 hover:text-red-700">
                  {t('common.delete')}
                </button>
              </>
            ) : (
              <button
                onClick={() => areaGpkgInputRef.current?.click()}
                disabled={areaGpkgBusy}
                className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
              >
                {areaGpkgBusy ? t('common.uploading') : t('common.addFile')}
              </button>
            )}
            <input ref={areaGpkgInputRef} type="file" accept=".gpkg" hidden onChange={handleAreaGpkgPick} />
          </div>
          <p className="text-[10px] text-slate-400">
            {t('cd.area.note')}
          </p>
        </div>

        <div className="mt-2 flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('cd.diff.namePlaceholder')}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            className="shrink-0 whitespace-nowrap px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white"
          >
            {submitting ? t('cd.diff.submitting') : t('cd.diff.submit')}
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-800 mb-2">{t('cd.diff.resultsTitle')}</h3>
        {diffs.length === 0 ? (
          <p className="text-xs text-slate-400">{t('cd.diff.noResults')}</p>
        ) : (
          <ul className="space-y-2">
            {diffs.map((d) => {
              const sum = summarize(d)
              const hasResult = sum.done > 0
              const shown = shownDiffId === d.id
              const renaming = renameId === d.id
              return (
                <li
                  key={d.id}
                  onClick={() => hasResult && !renaming && onToggleShow(d.id)}
                  aria-selected={shown}
                  className={`rounded-lg border border-l-4 p-3 transition ${
                    shown
                      ? 'border-blue-300 border-l-blue-600 bg-blue-50 ring-1 ring-blue-200'
                      : 'border-slate-200 border-l-transparent hover:bg-slate-50'
                  } ${hasResult && !renaming ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {renaming ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault()
                            handleRename(d.id)
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex gap-1"
                        >
                          <input
                            autoFocus
                            value={renameValue}
                            maxLength={100}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Escape' && setRenameId(null)}
                            className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <button type="submit" className="text-xs font-medium text-blue-600">
                            {t('common.save')}
                          </button>
                          <button type="button" onClick={() => setRenameId(null)} className="text-xs text-slate-400">
                            {t('common.cancel')}
                          </button>
                        </form>
                      ) : (
                        <p className="truncate text-sm font-medium text-slate-800">{d.name}</p>
                      )}
                      <p className="text-xs text-slate-500">
                        {t('cd.diff.rowMeta', {
                          type: d.type === 'A_B' ? t('cd.diff.typeAb') : t('cd.diff.typeTimeSeries'),
                          status: t(`cd.status.diff.${d.status}`),
                          count: d.items.length,
                        })}
                        {(d.areaWkt || d.areaGpkgFileName) && (
                          <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] text-violet-700">
                            {t('cd.diff.areaBadge', { mode: d.areaMode === 'EXCLUDE' ? t('common.exclude') : t('common.include') })}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>
                      {!renaming && (
                        <button
                          onClick={() => {
                            setRenameId(d.id)
                            setRenameValue(d.name)
                          }}
                          className="text-xs text-slate-400 hover:text-slate-700"
                        >
                          {t('cd.diff.rename')}
                        </button>
                      )}
                      {ACTIVE.has(d.status) && (
                        <button onClick={() => handleCancel(d.id)} className="text-xs text-slate-400 hover:text-slate-700">
                          {t('common.cancel')}
                        </button>
                      )}
                      <button onClick={() => handleDelete(d.id)} className="text-xs text-slate-400 hover:text-red-600">
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>

                  {hasResult && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                      <span className="font-semibold text-emerald-600">+{fmtVol(sum.added)}</span>
                      <span className="font-semibold text-red-600">-{fmtVol(sum.removed)}</span>
                      <span className={`font-bold ${sum.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {t('cd.diff.net', { value: `${sum.net >= 0 ? '+' : ''}${fmtVol(sum.net)}` })}
                      </span>
                    </div>
                  )}
                  {d.errorMessage && <p className="mt-1 text-xs text-red-600">{d.errorMessage}</p>}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {mapContainer && detail && detailItems.length > 0 && (
        <FloatingWindow
          title={t('cd.diff.detailTitle', { name: detail.name })}
          container={mapContainer}
          onClose={() => onToggleShow(detail.id)}
        >
          {renderDetail(detail)}
        </FloatingWindow>
      )}
    </div>
  )
}
